import Vue from 'vue'
import Router from 'vue-router'
import store from './store/store'

Vue.use(Router)

// Checks if a route has query params
// Used for keeping query params between routes
function hasQueryParams (route) {
  return !!Object.keys(route.query).length
}

// Wait for auth to be initialized before checking permissions
function waitForAuthInit () {
  return new Promise(function (resolve) {
    if (store.state.auth.initialized) {
      resolve()
      return
    }
    var unwatch = store.watch(
      function (state) { return state.auth.initialized },
      function (initialized) {
        if (initialized) {
          unwatch()
          resolve()
        }
      }
    )
  })
}

// Check if user has at least one of the required roles
function hasRequiredRole (allowedRoles) {
  if (!allowedRoles || allowedRoles.length === 0) {
    return true
  }
  var authUser = store.getters['auth/authUser']
  var isEkrmManager = store.getters['auth/isEkrmManager']
  var isDataUploader = store.getters['auth/isDataUploader']
  var isDiyModeler = store.getters['auth/isDiyModeler']

  return allowedRoles.some(function (role) {
    if (role === 'isEkrmManager') return isEkrmManager
    if (role === 'isDataUploader') return isDataUploader
    if (role === 'isDiyModeler') return isDiyModeler
    return authUser[role] === true
  })
}

// Check if current client has required feature enabled
function hasClientFeature (featureName) {
  if (!featureName) return true
  var currentClient = store.getters['clients/currentClientObject']
  return currentClient && currentClient[featureName] === true
}

let router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '/home',
      name: 'home',
      // route level code-splitting
      // this generates a separate chunk (about.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import(/* webpackChunkName: "home" */ './views/Home.vue')
    },
    {
      path: '/modelReport',
      name: 'modelReport',
      // route level code-splitting
      // this generates a separate chunk (about.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import(/* webpackChunkName: "modelReport" */ './views/ModelReport.vue')
    },
    {
      path: '/planOptimizer',
      name: 'planOptimizer',
      meta: { restrictedByModelReport: true },
      // route level code-splitting
      // this generates a separate chunk (about.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import(/* webpackChunkName: "planOptimizer" */ './views/planOptimizer.vue')
    },
    {
      path: '/planSimulator',
      name: 'planSimulator',
      meta: { restrictedByModelReport: true },
      // route level code-splitting
      // this generates a separate chunk (about.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import(/* webpackChunkName: "planSimulator" */ './views/planSimulator.vue')
    },
    {
      path: '/budgetSimulator',
      name: 'budgetSimulator',
      meta: { restrictedByModelReport: true },
      component: () => import(/* webpackChunkName: "budgetSimulator" */ './views/BudgetSimulator.vue')
    },
    {
      path: '/budgetSimulator/:id',
      name: 'budgetSimulationDetails',
      meta: { restrictedByModelReport: true },
      component: () => import(/* webpackChunkName: "budgetSimulator" */ './views/BudgetSimulationDetails.vue')
    },
    {
      path: '/simulationDetails',
      name: 'simulationDetails',
      meta: { restrictedByModelReport: true },
      // route level code-splitting
      // this generates a separate chunk (about.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import(/* webpackChunkName: "simulationDetails" */ './views/SimulationDetails.vue'),
      beforeEnter: (to, from, next) => {
        if ('simulation' in to.params) {
          next()
        } else {
          next('/modelReport')
        }
      }
    },
    {
      path: '/whatIfScenario',
      name: 'whatIfScenario',
      meta: { restrictedByModelReport: true },
      // route level code-splitting
      // this generates a separate chunk (about.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import(/* webpackChunkName: "whatIfScenario" */ './views/WhatIfScenario.vue')
    },
    {
      path: '/documents',
      name: 'documents',
      meta: { restrictedByModelReport: true },
      // route level code-splitting
      // this generates a separate chunk (about.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import(/* webpackChunkName: "documents" */ './views/Documents.vue')
    },
    {
      path: '/adminUsers',
      name: 'adminUsers',
      meta: { restrictedByModelReport: true, allowedRoles: ['isAdmin'] },
      // route level code-splitting
      // this generates a separate chunk (about.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import(/* webpackChunkName: "adminUsers" */ './views/adminUsers.vue')
    },
    {
      path: '/adminClients',
      name: 'adminClients',
      meta: { restrictedByModelReport: true, allowedRoles: ['isAdmin'] },
      // route level code-splitting
      // this generates a separate chunk (about.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import(/* webpackChunkName: "adminClients" */ './views/adminClients.vue')
    },
    {
      path: '/adminReferenceModels',
      name: 'adminReferenceModels',
      meta: { restrictedByModelReport: true, allowedRoles: ['isAdmin', 'isScientist'] },
      // route level code-splitting
      // this generates a separate chunk (about.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import(/* webpackChunkName: "adminReferenceModels" */ './views/adminReferenceModels.vue')
    },
    {
      path: '/adminAdHocModels',
      name: 'adminAdHocModels',
      meta: { restrictedByModelReport: true, allowedRoles: ['isAdmin', 'isScientist'] },
      // route level code-splitting
      // this generates a separate chunk (about.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import(/* webpackChunkName: "adminAdHocModels" */ './views/adminAdHocModels.vue')
    },
    {
      path: '/manageCustomModels',
      name: 'manageCustomModels',
      meta: { restrictedByModelReport: true, allowedRoles: ['isManager', 'isCustomizer', 'isEkrmManager'] },
      // route level code-splitting
      // this generates a separate chunk (about.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import(/* webpackChunkName: "manageCustomModels" */ './views/manageCustomModels.vue')
    },
    {
      path: '/manageEkrm',
      name: 'manageEkrm',
      meta: { restrictedByModelReport: true, allowedRoles: ['isScientist', 'isManager', 'isEkrmManager'] },
      // route level code-splitting
      // this generates a separate chunk (about.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import(/* webpackChunkName: "manageEkrm" */ './views/manageEkrm.vue')
    },
    {
      path: '/modelComposer',
      name: 'modelComposer',
      meta: { restrictedByModelReport: true, allowedRoles: ['isAdmin'] },
      component: () => import(/* webpackChunkName: "modelComposer" */ './views/modelComposer.vue')
    },
    {
      path: '/automaticModels',
      name: 'automaticModels',
      meta: { restrictedByModelReport: true, allowedRoles: ['isAdmin', 'isScientist', 'isManager', 'isDiyModeler'] },
      component: () => import(/* webpackChunkName: "automaticModels" */ './views/automaticModels.vue')
    },
    {
      path: '/manageUserModels',
      name: 'manageUserModels',
      meta: { restrictedByModelReport: true, allowedRoles: ['isManager', 'isSupervisor'] },
      // route level code-splitting
      // this generates a separate chunk (about.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import(/* webpackChunkName: "manageUserModels" */ './views/manageUserModels.vue')
    },
    {
      path: '/learningCenter',
      name: 'learningCenter',
      meta: { restrictedByModelReport: true },
      // route level code-splitting
      // this generates a separate chunk (about.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import(/* webpackChunkName: "learningCenter" */ './views/learningCenter.vue')
    },
    {
      path: '/stats',
      name: 'stats',
      meta: { restrictedByModelReport: true, allowedRoles: ['isAdmin'] },
      // route level code-splitting
      // this generates a separate chunk (about.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import(/* webpackChunkName: "stats" */ './views/stats.vue')
    },
    {
      path: '/adminTools',
      name: 'adminTools',
      // Scientists get in for the ngtorch override card only; the other tools stay admin-only.
      meta: { restrictedByModelReport: true, allowedRoles: ['isAdmin', 'isScientist'] },
      // route level code-splitting
      // this generates a separate chunk (about.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import(/* webpackChunkName: "adminTools" */ './views/adminTools.vue')
    },
    {
      path: '/adminPendingPlans',
      name: 'adminPendingPlans',
      meta: { restrictedByModelReport: true, allowedRoles: ['isAdmin'] },
      // route level code-splitting
      // this generates a separate chunk (about.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import(/* webpackChunkName: "adminPendingPlans" */ './views/adminPendingPlans.vue')
    },
    {
      path: '/adminAIReviews',
      name: 'adminAIReviews',
      meta: { restrictedByModelReport: true, allowedRoles: ['isAdmin'] },
      // route level code-splitting
      // this generates a separate chunk (about.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import(/* webpackChunkName: "adminAIReviews" */ './views/adminAIReviews.vue')
    },
    {
      path: '/adminErrorLogs',
      name: 'adminErrorLogs',
      meta: { restrictedByModelReport: true, allowedRoles: ['isAdmin'] },
      // route level code-splitting
      // this generates a separate chunk (about.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import(/* webpackChunkName: "adminErrorLogs" */ './views/adminErrorLogs.vue')
    },
    {
      path: '/adminNews',
      name: 'adminNews',
      meta: { restrictedByModelReport: true, allowedRoles: ['isAdmin'] },
      // route level code-splitting
      // this generates a separate chunk (about.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import(/* webpackChunkName: "adminNews" */ './views/adminNews.vue')
    },
    {
      path: '/adminCountries',
      name: 'adminCountries',
      meta: { restrictedByModelReport: true, allowedRoles: ['isAdmin', 'isScientist'] },
      // route level code-splitting
      // this generates a separate chunk (about.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import(/* webpackChunkName: "adminCountries" */ './views/adminCountries.vue')
    },
    {
      path: '/insertMap',
      name: 'insertMap',
      meta: { restrictedByModelReport: true, allowedRoles: ['isAdmin', 'isScientist'] },
      // route level code-splitting
      // this generates a separate chunk (about.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import(/* webpackChunkName: "insertMap" */ './views/InsertMap.vue')
    },
    {
      path: '/playground',
      name: 'playground',
      meta: { restrictedByModelReport: true, allowedRoles: ['isAdmin', 'isScientist'] },
      // route level code-splitting
      // this generates a separate chunk (about.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import(/* webpackChunkName: "playground" */ './views/playground.vue')
    },
    {
      path: '/dataUpload',
      name: 'dataUpload',
      meta: { restrictedByModelReport: true, allowedRoles: ['isAdmin', 'isScientist', 'isManager', 'isDataUploader'], requiresClientFeature: 'enableDataUploader' },
      // route level code-splitting
      // this generates a separate chunk (about.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import(/* webpackChunkName: "dataUpload" */ './views/DataUpload.vue')
    },
    {
      path: '/dataCheck',
      name: 'dataCheck',
      meta: { restrictedByModelReport: true, allowedRoles: ['isAdmin', 'isScientist', 'isManager', 'isDataUploader'], requiresClientFeature: 'enableDataUploader' },
      component: () => import(/* webpackChunkName: "dataCheck" */ './views/DataCheck.vue')
    },
    {
      path: '/modelingProcess',
      name: 'modelingProcess',
      meta: { restrictedByModelReport: true },
      // route level code-splitting
      // this generates a separate chunk (about.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import(/* webpackChunkName: "modelingProcess" */ './views/ModelingProcess.vue')
    },
    {
      path: '/sharedPlan/:shareId',
      name: 'sharedPlan',
      meta: { public: true },
      // Lazy-loaded component, following the pattern used in other routes
      component: () => import(/* webpackChunkName: "sharedPlan" */ './views/SharedPlan.vue'),
      props: true,
      // Skip the beforeEach handler that copies query params
      beforeEnter: (to, from, next) => {
        next()
      }
    },
    {
      path: '/sharedModelReport/:shareId',
      name: 'sharedModelReport',
      meta: { public: true },
      // Lazy-loaded component, following the pattern used in other routes
      component: () => import(/* webpackChunkName: "sharedModelReport" */ './views/SharedModelReport.vue'),
      props: true,
      // Skip the beforeEach handler that copies query params
      beforeEnter: (to, from, next) => {
        next()
      }
    },
    {
      path: '/export/plan/:clientId/:simulationId',
      name: 'exportPlan',
      meta: { restrictedByModelReport: true, allowedRoles: ['isAdmin', 'isScientist'] },
      // route level code-splitting
      // this generates a separate chunk (about.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import(/* webpackChunkName: "exportData" */ './views/ExportData.vue'),
      props: route => ({
        type: 'plan',
        clientId: route.params.clientId,
        simulationId: route.params.simulationId
      })
    },
    {
      path: '/export/model/:clientId',
      name: 'exportModel',
      meta: { restrictedByModelReport: true, allowedRoles: ['isAdmin', 'isScientist'] },
      // route level code-splitting
      // this generates a separate chunk (about.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import(/* webpackChunkName: "exportData" */ './views/ExportData.vue'),
      props: route => ({
        type: 'model',
        clientId: route.params.clientId
      })
    },
    {
      path: '/',
      redirect: '/home'
    }
  ],
  scrollBehavior (to, from, savedPosition) {
    return { x: 0, y: 0 }
  }
})

router.beforeEach(function (to, from, next) {
  // Public routes don't need auth check
  if (to.meta && to.meta.public) {
    next()
    return
  }

  // Check if route requires any permission checks
  var requiresPermissionCheck = to.meta && (
    to.meta.restrictedByModelReport ||
    to.meta.allowedRoles ||
    to.meta.requiresClientFeature
  )

  // If no permission checks required, allow navigation but still preserve query params
  if (!requiresPermissionCheck) {
    if (to.name !== 'sharedPlan' && to.name !== 'sharedModelReport') {
      if (!hasQueryParams(to) && hasQueryParams(from)) {
        next(Object.assign({}, to, { query: from.query }))
        return
      }
    }
    next()
    return
  }

  // For routes with permission requirements, wait for auth to initialize
  waitForAuthInit().then(function () {
    var authUser = store.getters['auth/authUser']
    var shouldShowOnlyModelReport = store.getters['auth/shouldShowOnlyModelReport']

    // Check if user is logged in
    if (!authUser.isLoggedIn) {
      next({ name: 'home' })
      return
    }

    // Check if route is restricted for "only model report" users
    if (to.meta.restrictedByModelReport && shouldShowOnlyModelReport) {
      next({ name: 'modelReport' })
      return
    }

    // Check role-based access
    if (to.meta.allowedRoles) {
      if (!hasRequiredRole(to.meta.allowedRoles)) {
        next({ name: 'home' })
        return
      }
    }

    // Check client feature requirements
    if (to.meta.requiresClientFeature) {
      if (!hasClientFeature(to.meta.requiresClientFeature)) {
        next({ name: 'home' })
        return
      }
    }

    // Existing query param preservation logic
    if (to.name !== 'sharedPlan' && to.name !== 'sharedModelReport') {
      if (!hasQueryParams(to) && hasQueryParams(from)) {
        next(Object.assign({}, to, { query: from.query }))
        return
      }
    }

    next()
  })
})

// Recover from stale code-split chunks after a new deploy. When an already-open
// tab still runs the previous app shell, the hashed chunk it lazy-imports no
// longer exists; Firebase's SPA rewrite then serves index.html (text/html) for
// it, which surfaces as a ChunkLoadError. Force a one-time reload to pull the
// fresh index.html (now no-cache) and its current chunk references. A short
// timestamp guard prevents a reload loop if the reload doesn't resolve it.
router.onError(function (error) {
  var message = error && error.message ? error.message : ''
  var isChunkLoadError = (error && error.name === 'ChunkLoadError') ||
    /ChunkLoadError/.test(message) ||
    /Loading (CSS )?chunk [^ ]+ failed/i.test(message)
  if (!isChunkLoadError) {
    return
  }
  var now = Date.now()
  var last = Number(sessionStorage.getItem('ng_chunk_reload_at') || 0)
  if (now - last < 10000) {
    return
  }
  sessionStorage.setItem('ng_chunk_reload_at', String(now))
  window.location.reload()
})

export default router
